import { FC, useEffect, useState } from 'react'
import moment from 'moment'
import { useMySelector } from '../../shared/hooks/useMySelector'
import { useTranslations } from '../../shared/hooks/useTranslations'
import { handleLocalStorage } from '../../shared/utils/handleLocalStorage'
import { IRestaurant } from 'featureSliced/shared/store/reducers/RestaurantsReducer/types'
import { ScheduleType, week, weekArr } from '../../../components/Schedule/types'

import { RenderIf } from '../../../components/RenderIf'
import { Schedule } from '../../../components/Schedule'
import { Spinner } from 'featureSliced/entities/Spinner'
import { Button } from '../../../components/Button'
import { Input } from '../../../components/Input'

import qrMenuLogo from 'assets/prompt/qrMenu.svg'
import { SelectTime } from './styled'

import {
  TimeButtonContainer,
  RestaurantsCheckBox,
  RestaurantsContainer,
  RestaurantsItem,
  RestaurantsTitle,
  UnavailableSign,
  RestaurantsAddress,
} from './styledComponents'
import { TableReservationSlice } from 'featureSliced/shared/store/reducers/TableReservation'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'

type BranchPickerProps = {
  isWallet?: boolean
  increaseStep: () => void
  // Called once the restaurants fetch has settled with nothing to pick from —
  // only wired by the forced-prompt caller (usePromptParams); the Wallet call
  // site never renders this component unless it already knows restaurants is
  // non-empty, so it passes nothing here.
  onNoBranches?: () => void
}

export const BranchPicker: FC<BranchPickerProps> = ({
  isWallet,
  increaseStep,
  onNoBranches,
}) => {
  // Hooks
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const { restaurants, restaurantsStatus } = useMySelector((state) => state)

  // State
  // `restaurants` starts as `[]` (truthy) before the fetch even runs, so it
  // can't tell "not loaded yet" from "loaded, nothing here" — restaurantsStatus
  // is the real signal (set once getRestaurants() has resolved either way).
  const isLoading = !restaurantsStatus.settled
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)
  // const [selectedId, setSelectedId] = useState<number>(0)
  const [selectedHash, setSelectedHash] = useState<string>()
  const [selectedBranchId, setSelectedBranchId] = useState<number>(0)
  const { logoImage } = useMySelector((state) => state.app.interfaceStyles)
  const [timeFilteredRestaurants, setTimeFilteredRestaurants] = useState<
    IRestaurant[]
  >([])
  const [restaurantsToShow, setRestaurantsToShow] = useState<IRestaurant[]>([])

  const updateSelectedBranchId =
    TableReservationSlice.actions.updateSelectedBranchId

  // useEffects
  useEffect(() => {
    if (restaurants) {
      const { pickUpTime } = handleLocalStorage(['pickUpTime'])
      const format = 'HH:mm:ss'
      const pickupTimeFormatted = pickUpTime ? moment(pickUpTime) : moment()
      const restaurantsFiltered = restaurants.reduce(
        (acc: IRestaurant[], restaurant: IRestaurant) => {
          const currentSchedule =
            restaurant.schedule[
              pickupTimeFormatted
                ?.lang('en')
                ?.format('dddd')
                ?.toLocaleLowerCase()
            ]
          if (process.env.REACT_APP_IS_CLIC === '0') {
            return [
              ...acc,
              {
                ...restaurant,
                isAvailable: true,
              },
            ]
          } else if (currentSchedule) {
            const [startString, endString] = currentSchedule.split(' - ') || []
            const startTime = moment(startString, format)
            const endTime = moment(endString, format)
            const isBetween = moment(
              pickupTimeFormatted.format(format),
              format
            ).isBetween(startTime, endTime, undefined, '[)')
            if (isBetween) {
              return [
                ...acc,
                {
                  ...restaurant,
                  isAvailable: true,
                },
              ]
            } else {
              return [
                ...acc,
                {
                  ...restaurant,
                  isAvailable: false,
                },
              ]
            }
          } else {
            return [
              ...acc,
              {
                ...restaurant,
                isAvailable: false,
              },
            ]
          }
        },
        []
      )
      setTimeFilteredRestaurants(restaurantsFiltered)
    }
  }, [restaurants])

  useEffect(() => {
    if (timeFilteredRestaurants && timeFilteredRestaurants.length === 1) {
      dispatch(updateSelectedBranchId(timeFilteredRestaurants[0].id))
      if (timeFilteredRestaurants[0].dataEnc) {
        handleLocalStorage({
          branchId: timeFilteredRestaurants[0].id,
          dataEnc: timeFilteredRestaurants[0].dataEnc,
        })
      } else {
        handleLocalStorage({
          branchId: timeFilteredRestaurants[0].id,
          registerId: timeFilteredRestaurants[0].clickAndCollectClientId,
        })
      }
      increaseStep()
    }
    if (timeFilteredRestaurants && timeFilteredRestaurants.length > 1) {
      setRestaurantsToShow(timeFilteredRestaurants)
    }
  }, [timeFilteredRestaurants])

  // Nothing to pick once the fetch has genuinely finished — bail out instead
  // of leaving the guest in a modal with a title and nothing else (no list,
  // no accept, and this prompt hides its close cross). Gated on `settled`,
  // never on the array being empty on its own: it starts empty on every
  // mount before the fetch has even run.
  useEffect(() => {
    if (
      restaurantsStatus.settled &&
      timeFilteredRestaurants &&
      timeFilteredRestaurants.length === 0
    ) {
      onNoBranches?.()
    }
  }, [restaurantsStatus.settled, timeFilteredRestaurants])

  // Functions
  const filterRestaurants = (value: string) => {
    if (timeFilteredRestaurants) {
      setRestaurantsToShow(
        timeFilteredRestaurants.filter((restaurant) =>
          restaurant.title
            .toLocaleLowerCase()
            .includes(value.toLocaleLowerCase())
        )
      )
    }
  }

  const handleRestaurantClick = (restaurant: IRestaurant) => {
    if (restaurant?.isAvailable || isWallet) {
      setSelectedBranchId(restaurant.id)
      setSelectedHash(restaurant.dataEnc)
    }
  }

  const handleBranchAccept = () => {
    setIsButtonLoading(true)
    dispatch(updateSelectedBranchId(selectedBranchId))
    if (selectedHash) {
      handleLocalStorage({
        branchId: selectedBranchId,
        dataEnc: selectedHash,
      })
    } else {
      handleLocalStorage({
        branchId: selectedBranchId,
        registerId: restaurants?.find((rest) => rest.id === selectedBranchId)
          ?.clickAndCollectClientId,
      })
    }
    increaseStep()
  }

  return (
    <div>
      <RenderIf condition={isLoading}>
        <Spinner type="alternate" />
      </RenderIf>
      <SelectTime>
        {t(
          process.env.REACT_APP_IS_CLIC === '0'
            ? 'scanner.selectBranch'
            : 'clickCollect.choosePickUpLocation'
        )}
        {/*  */}
      </SelectTime>
      <RenderIf condition={!!timeFilteredRestaurants?.length}>
        <Input onSearch={(e: string) => filterRestaurants(e)} />
        <RestaurantsContainer>
          {restaurantsToShow
            ?.sort((a, b) => Number(b.isAvailable) - Number(a.isAvailable))
            ?.map((restaurant) => (
              <RestaurantsItem
                key={`restaurant_${restaurant.id}`}
                onClick={() => handleRestaurantClick(restaurant)}
                isAvailable={restaurant?.isAvailable || Boolean(isWallet)}
                isQRMenu={process.env.REACT_APP_IS_CLIC === '0'}
                isSelected={restaurant.dataEnc === selectedHash}
              >
                <div>
                  <img
                    style={{
                      objectFit: 'contain',
                      // filter: 'drop-shadow(5px 5px 10px #00000060)',
                    }}
                    src={
                      restaurant?.imageData?.logoUrl || logoImage || qrMenuLogo
                    }
                    alt="Branch"
                    height="95"
                    width="95"
                  />
                </div>
                <div>
                  <RenderIf
                    condition={!!restaurant?.isAvailable || Boolean(isWallet)}
                  >
                    <RestaurantsCheckBox
                      isSelected={restaurant.dataEnc === selectedHash}
                    />
                  </RenderIf>
                  <RestaurantsTitle>{restaurant.title}</RestaurantsTitle>
                  <RestaurantsAddress>
                    {restaurant?.address || ''}
                  </RestaurantsAddress>
                  <Schedule schedule={restaurant.schedule} />
                </div>
                <RenderIf condition={!restaurant?.isAvailable && !isWallet}>
                  <UnavailableSign>
                    {t('clickCollect.unavailableBySchedule')}
                  </UnavailableSign>
                </RenderIf>
              </RestaurantsItem>
            ))}
        </RestaurantsContainer>
        <TimeButtonContainer>
          <Button
            buttonType={selectedHash?.length ? 'common' : 'gray'}
            disabled={!selectedHash?.length}
            width="100%"
            onClick={handleBranchAccept}
            isLoading={isButtonLoading}
          >
            {t('buttons.accept')}
          </Button>
        </TimeButtonContainer>
      </RenderIf>
    </div>
  )
}

export default BranchPicker
